/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   LicenseGate.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (US4)
 *     Description     :   Wraps the app shell (AppLayout) and renders from the session licence
 *                         (contracts/portal-gating.md "SPA"): exempt / active → the app; plugged → the
 *                         subscription notice page; unverifiable → the can't-verify page with a retry
 *                         that re-reads /api/auth/session. No licence yet (or an older backend that does
 *                         not send one) → the app, as before. Placed around AppLayout rather than in
 *                         auth/ProtectedRoute, which wraps routes INSIDE the shell — the pages must
 *                         replace the navigation, not sit beneath it.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React, { useState } from 'react';
import axios from 'axios';
import type { SessionInfo } from '@shared/types/auth';
import { useAuthStore } from '../stores/authStore';
import { useAuth } from './hooks/useAuth';
import { devRoleHeaders } from './devUserStore';
import { applySessionLicence } from './licenceSession';
import { SubscriptionNoticePage } from '../pages/license/SubscriptionNoticePage';
import { LicenseUnverifiablePage } from '../pages/license/LicenseUnverifiablePage';

export const LicenseGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const license = useAuthStore((s) => s.license);
  const { logout } = useAuth();
  const [retrying, setRetrying] = useState(false);

  const signOut = () => { void logout(); };

  const retry = async () => {
    setRetrying(true);
    try {
      const response = await axios.get<SessionInfo>('/api/auth/session', { headers: devRoleHeaders() });
      applySessionLicence(response.data);
    } catch {
      // Still unreadable (or the session itself failed): stay on this page; the user can retry.
    } finally {
      setRetrying(false);
    }
  };

  if (license?.state === 'plugged') {
    return <SubscriptionNoticePage reason={license.plugReason ?? 'unknown-value'} onSignOut={signOut} />;
  }
  if (license?.state === 'unverifiable') {
    return <LicenseUnverifiablePage onRetry={() => { void retry(); }} retrying={retrying} onSignOut={signOut} />;
  }
  return <>{children}</>;
};

export default LicenseGate;
